import { useCallback, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  throwIfValidationRejected,
  useValidationFallback,
  type SaveState,
} from "@/components/match/use-match-editor";
import { FormattedMessage, useIntl } from "react-intl";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { ProjectSettingsModal } from "./ProjectSettingsModal";
import { axesToRole, roleToAxes, type ProjectRole } from "./project-access";
import { BackButton } from "@/components/common/BackButton";
import { ShareDetailStatus } from "@/components/share/ShareDetailStatus";
import {
  CreateProjectDocument,
  DeleteProjectDocument,
  ProjectDocument,
  UnlinkProjectMatchDocument,
  UpdateProjectDocument,
} from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { useMe } from "@/auth/use-me";
import type { PersonLinkPerson } from "@/components/common/PersonLink";
import { ProjectDocuments, type ProjectDocumentRef } from "./ProjectDocuments";
import {
  FACET_ACTIVITY,
  PROJECT_FACETS,
  type Msg,
  type ProjectFacetKey,
  type ProjectMockStep,
} from "./project-mock-data";
import {
  EMPTY_REQUIREMENTS,
  parseCandidates,
  parseEvents,
  parsePackages,
  parseRequirements,
  parseStatus,
  type PlanPerson,
  type ProjectEvent,
  type ProjectPackage,
  type ProjectRequirements,
  type ProjectStatus,
} from "./project-plan";
import { useProjectPeople } from "./use-project-people";
import { useLinkedMatches } from "./use-linked-matches";
import { ProjectActivity } from "./ProjectActivity";
import { ProjectWorkspace, type ProjectActivityItem } from "./ProjectWorkspace";
import { ProjectCreateScreen } from "./screens/ProjectCreateScreen";
import { ProjectGuidedAufteilenScreen } from "./screens/ProjectGuidedAufteilenScreen";
import { ProjectGuidedTeamScreen } from "./screens/ProjectGuidedTeamScreen";
import { ProjectCandidatesScreen, type CandidatePath } from "./screens/ProjectCandidatesScreen";
import { ProjectTeamScreen } from "./screens/ProjectTeamScreen";
import { ProjectToMatchScreen } from "./screens/ProjectToMatchScreen";

// Initial persisted Project prototype. Creation, planning data and match links
// use genesis!446. Sharing, deletion, company context and the feature gate are
// unfinished; see docs/project-prototype.md for the delivery boundary.

// Wer ein Projekt gerade anlegt, ist seine Eigentümerin und darf alles.
const CREATOR_ACTIONS = { canUpdate: true, canManageAccess: true, canDelete: true } as const;

export function ProjectMockPage() {
  const intl = useIntl();
  const me = useMe();
  const [project, setProject] = useState<{ title: string; description: string } | null>(null);
  const [step, setStep] = useState<ProjectMockStep>("create");
  const [done, setDone] = useState<ReadonlySet<ProjectFacetKey>>(() => new Set<ProjectFacetKey>());
  const [activity, setActivity] = useState<readonly ProjectActivityItem[]>([]);
  // Die Pakete liegen auf Seitenebene, nicht im Aufteilen-Screen: der Ablauf
  // wird dort festgelegt, aber die Timeline zeigt ihn - zwei Screens, ein
  // Datenstand. Im echten Produkt wäre das der Project-Aggregat, hier reicht
  // ein useState an derselben Stelle.
  const [packages, setPackages] = useState<readonly ProjectPackage[]>([]);
  // Termine liegen neben den Paketen, nicht in ihnen: ein Zeitpunkt gehört dem
  // Projekt, ein Zeitraum dem Arbeitspaket.
  const [events, setEvents] = useState<readonly ProjectEvent[]>([]);
  // Das Anforderungsprofil: Fachwissen und Power-Gewichte. Leer, bis jemand
  // etwas einträgt - ein Bedarf, den niemand angemeldet hat, wäre eine Vorgabe,
  // die man übersieht und nach der dann gesucht wird.
  const [requirements, setRequirements] = useState<ProjectRequirements>(EMPTY_REQUIREMENTS);
  /** Person-Ids der Gefragten. Noch keine Mitglieder, aber schon einplanbar. */
  const [candidates, setCandidates] = useState<readonly string[]>([]);
  /** Der Lebenszustand, vom Menschen gesetzt statt aus der Maske abgeleitet. */
  const [status, setStatus] = useState<ProjectStatus>("IDEA");

  // Der Anlass ist eine einzelne Auswahl und lebt deshalb als Wert hier, nicht
  // als eigener Schritt im Assistenten.
  const [anlassId, setAnlassId] = useState<string | null>(null);
  const [tags, setTags] = useState<readonly string[]>([]);
  /**
   * Die Flächen, die es ins gespeicherte Projekt geschafft haben. Teilmenge von
   * `done`: dort stehen auch die gerade geöffneten, in denen noch nichts steht.
   */
  const [sections, setSections] = useState<readonly ProjectFacetKey[]>([]);
  const [documents, setDocuments] = useState<readonly ProjectDocumentRef[]>([]);
  // Welcher Weg im Match-Reiter offen ist. Hier oben, weil auch der Team-Reiter
  // hinüberspringt und dabei sagt, welcher Weg sich öffnen soll.
  const [candidatePath, setCandidatePath] = useState<CandidatePath>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const navigate = useNavigate();
  /** Welcher Reiter im Projektblatt offen ist. "overview" plus je ein Reiter pro Fläche. */
  // Der offene Reiter steht in der Adresse, nicht nur im Zustand. Sonst landet
  // man nach einem Abstecher aufs Profil einer Person über den Zurück-Knopf
  // wieder in der Übersicht, obwohl man im Team war - und sucht sich neu.
  // `replace`, damit die Reiterwechsel keine eigene Geschichte anlegen, durch
  // die der Zurück-Knopf sich erst hindurchklicken müsste.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "overview";
  const setActiveTab = useCallback(
    (next: string) => {
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next === "overview") params.delete("tab");
          else params.set("tab", next);
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
  /** Angelegt-Zeile: kommt aus genesis, sobald das Projekt geladen wurde. */
  const [createdMeta, setCreatedMeta] = useState<{ at: string; by: PersonLinkPerson } | null>(null);
  // Ab hier ist das kein Mock mehr: Anlegen schreibt in genesis (Feature-Instanz
  // auf 18094, eigene Datenbank). Alles Weitere lebt weiterhin nur im Zustand
  // dieser Seite, bis die passenden Mutationen dran sind.
  const [projectId, setProjectId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Genesis lehnt eine Eingabe nicht mit einem Transportfehler ab, sondern mit
  // einer zweiten Ausprägung der Ergebnis-Union. Apollo wirft dafür nicht - ohne
  // die Prüfung unten liefe eine Ablehnung als Erfolg durch. Meldung und Helfer
  // kommen aus dem Match-Editor; derselbe Satz braucht keinen zweiten Katalogeintrag.
  const validationFallback = useValidationFallback();
  const [createProject, { loading: creating }] = useMutation(CreateProjectDocument);
  const [updateProject] = useMutation(UpdateProjectDocument);
  const [deleteProject, { loading: deleting }] = useMutation(DeleteProjectDocument);
  // Die Antwort trägt Project.matches; der Cache ersetzt damit die Liste, die Kachel verschwindet von selbst.
  const [unlinkProjectMatch] = useMutation(UnlinkProjectMatchDocument);

  // Autosave nach demselben Muster wie der Match-Editor
  // (@adr SPARK-0008-match-editor-autosave@1): eine Änderung plant einen Schreibvorgang
  // etwa eine Sekunde nach der letzten Eingabe, das Verlassen eines Feldes zieht ihn
  // vor. Die Zustandsanzeige ist dieselbe Komponente wie dort.
  //
  // Warum hier eigener Code und nicht `useMatchEditor`: dieser Hook ist 1170 Zeilen und
  // steckt voller Match-Wissen (Design-Umwandlung, Zugriffsachsen prepare/design,
  // Cache-Chirurgie für Kandidaten). Herausgelöst würde davon ein Debounce mit vier
  // Zuständen übrig bleiben - das steht unten. Sobald ein dritter Aufrufer kommt, gehört
  // genau dieser Kern in einen gemeinsamen Hook.
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [lastSavedAt, setLastSavedAt] = useState<number | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ name?: string; description?: string; data?: Record<string, unknown> }>({});

  const flush = useCallback(async () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    const patch = pending.current;
    if (!projectId || Object.keys(patch).length === 0) return;
    pending.current = {};
    setSaveState("saving");
    try {
      const res = await updateProject({ variables: { id: projectId, input: { data: patch } } });
      throwIfValidationRejected(res.data?.updateProject, "ValidateProjectResult", validationFallback);
      setLastSavedAt(Date.now());
      setSaveState("idle");
      setSaveError(null);
    } catch (e) {
      // Der Ausschnitt bleibt liegen, damit der Wiederholen-Knopf etwas zu senden hat.
      pending.current = { ...patch, ...pending.current };
      setSaveState("error");
      setSaveError(userErrorMessage(intl, e));
    }
  }, [intl, projectId, updateProject, validationFallback]);

  const schedule = useCallback(
    (patch: { name?: string; description?: string; data?: Record<string, unknown> }) => {
      // data-Ausschnitte werden auch lokal zusammengeführt, sonst verlöre ein zweiter
      // Tastendruck den ersten, bevor überhaupt gesendet wird.
      pending.current = {
        ...pending.current,
        ...patch,
        ...(patch.data ? { data: { ...(pending.current.data ?? {}), ...patch.data } } : {}),
      };
      setSaveState("pending");
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => void flush(), 1000);
    },
    [flush],
  );

  /** Tippen: lokal sofort, gespeichert wird gebündelt. */
  function editProject(patch: { title?: string; description?: string }) {
    setProject((cur) => (cur === null ? cur : { ...cur, ...patch }));
    schedule({
      ...(patch.title !== undefined ? { name: patch.title } : {}),
      ...(patch.description !== undefined ? { description: patch.description } : {}),
    });
  }

  /** Schreibt einen Ausschnitt des data-Dokuments; genesis mischt ihn in den Bestand. */
  function commitData(patch: Record<string, unknown>) {
    schedule({ data: patch });
  }

  /** Feld verlassen: den geplanten Schreibvorgang vorziehen. */
  function commitProject() {
    void flush();
  }

  // Mit Id in der Route ist das Projekt bereits angelegt: laden statt anlegen.
  const { projectId: routeProjectId } = useParams();
  const loaded = useQuery(ProjectDocument, {
    variables: { id: routeProjectId ?? "" },
    skip: !routeProjectId,
  });
  const loadedProject = loaded.data?.project ?? null;
  // Was ich am Projekt darf, sagt genesis wie im Match (Project.actions), nicht die Rolle aus der Zugriffsliste.
  const actions = loadedProject?.actions ?? CREATOR_ACTIONS;
  const readOnly = !actions.canUpdate;
  // Die Zugriffszeilen für die Einstellungen. Der Ersteller bleibt draussen: er
  // sieht das Projekt, weil er es angelegt hat, und dürfte sich sonst selbst
  // herauswerfen.
  const accessRows = useMemo(
    () =>
      (loadedProject?.access ?? []).flatMap((entry) =>
        entry?.person && entry.person.id !== loadedProject?.createdBy?.id
          ? [
              {
                person: {
                  id: entry.person.id,
                  name: entry.person.formattedName ?? "",
                  avatarContent: entry.person.avatar?.content,
                  avatarMime: entry.person.avatar?.mimeType,
                },
                role: axesToRole({
                  plan: entry.plan,
                  candidates: entry.candidates,
                  timeline: entry.timeline,
                }),
              },
            ]
          : [],
      ),
    [loadedProject],
  );
  // Wer im Projekt ist, gebaut wie im Match (MatchSettingsModal): die Eigentümerin
  // kommt aus createdBy, beim frischen Anlegen bin ich es, und neben ihr steht die
  // Zugriffsliste ohne sie. Einstellungen, Team-Reiter und Zuweisung lesen alle aus
  // diesen beiden, damit sie nicht auseinanderlaufen (spark#1001).
  const owner = useMemo<PlanPerson | null>(
    () => (createdMeta?.by.id ? { ...createdMeta.by, id: createdMeta.by.id } : null),
    [createdMeta],
  );
  const others = useMemo(() => accessRows.map((row) => row.person), [accessRows]);
  const members = useMemo(() => (owner ? [owner, ...others] : others), [owner, others]);
  // Wer zuweisbar ist und wie die Zugewiesenen heißen. Hängt am Plan, weil die
  // Spuren des Personfokus den tatsächlich Beteiligten folgen.
  const people = useProjectPeople(packages, members, candidates);
  // Die Matches, die aus diesem Projekt entstanden sind. Die Relation lebt in
  // genesis (project_match), nicht im Dokument; die vollen Daten für die Kachel
  // holt der Hook über die bekannten Ids nach.
  const linkedMatchIds = useMemo(
    () => (loadedProject?.matches ?? []).map((m) => m.id),
    [loadedProject],
  );
  const linkedMatches = useLinkedMatches(linkedMatchIds);
  if (routeProjectId && loadedProject && project === null) {
    // Einmalig beim ersten Rendern mit Daten - kein Effekt nötig, weil die
    // Bedingung danach nie wieder wahr ist (@adr SPARK-0005-unified-deferred-save-bar@1 gilt für Speichern,
    // nicht für das Setzen eines geladenen Stands).
    setProject({ title: loadedProject.name ?? "", description: loadedProject.description ?? "" });
    setProjectId(loadedProject.id);
    // Das data-Dokument ist ein Object-Skalar, im Codegen also untypisiert.
    // Genau hier ist die Grenze zwischen losem JSONB und getippter Oberfläche,
    // und sie gehört an EINE Stelle statt in jede Komponente.
    const data = (loadedProject.data ?? {}) as {
      tags?: unknown;
      anlass?: unknown;
      documents?: unknown;
      sections?: unknown;
      packages?: unknown;
      events?: unknown;
      requirements?: unknown;
      candidates?: unknown;
      status?: unknown;
    };
    setStatus(parseStatus(data.status));
    setPackages(parsePackages(data.packages));
    setEvents(parseEvents(data.events));
    setRequirements(parseRequirements(data.requirements));
    const gefragte = parseCandidates(data.candidates);
    setCandidates(gefragte);
    if (Array.isArray(data.tags)) setTags(data.tags.filter((x): x is string => typeof x === "string"));
    if (Array.isArray(data.documents)) {
      setDocuments(
        data.documents.filter(
          (d): d is ProjectDocumentRef =>
            typeof d === "object" && d !== null && typeof (d as ProjectDocumentRef).id === "string",
        ),
      );
    }
    // Unbekannte Schlüssel fliegen raus: eine umbenannte oder entfallene Fläche
    // soll keinen toten Reiter hinterlassen.
    const gespeicherte = Array.isArray(data.sections)
      ? data.sections.filter((x): x is ProjectFacetKey =>
          PROJECT_FACETS.some((f) => f.key === x && f.key !== "anlass"),
        )
      : [];
    setSections(gespeicherte);
    const wiederDa = new Set<ProjectFacetKey>(gespeicherte);
    // Der Anlass ist ein Feld, keine Fläche - er zählt zum Fortschritt, sobald er gesetzt ist.
    if (typeof data.anlass === "string") {
      setAnlassId(data.anlass);
      wiederDa.add("anlass");
    }
    // Wer Menschen im Projekt hat, hat ein Team - auch wenn die Fläche nie als
    // Pille geholt wurde. Sie ergibt sich aus den Daten, nicht aus einem Klick.
    if (gefragte.length > 0) wiederDa.add("mitglieder");
    // Dasselbe für die Unterlagen: wer welche abgelegt hat, hat die Fläche.
    if (Array.isArray(data.documents) && data.documents.length > 0) wiederDa.add("dokumente");
    setDone(wiederDa);
    const creator = loadedProject.createdBy;
    const creatorName = [creator?.name?.given?.join(" "), creator?.name?.family].filter(Boolean).join(" ");
    setCreatedMeta({
      at: loadedProject.createdAt ?? "",
      by: {
        id: creator?.id,
        name: creatorName,
        avatarContent: creator?.avatar?.content,
        avatarMime: creator?.avatar?.mimeType,
      },
    });
    setStep("overview");
  }

  function log(id: string, message: Msg) {
    // Zeitpunkt wird beim Eintragen festgehalten, nicht beim Anzeigen: eine Zeile
    // soll später dasselbe Datum tragen wie in dem Moment, in dem sie entstand.
    setActivity((prev) =>
      prev.some((a) => a.id === id) ? prev : [...prev, { id, message, at: new Date().toISOString() }]);
  }

  /**
   * Der Plan ändert sich: sofort sichtbar, gebündelt gespeichert. Damit ist die
   * Aufteilung zugleich befüllt, also gehört sie ab hier zum Projekt.
   */
  function editPackages(next: readonly ProjectPackage[]) {
    setPackages(next);
    commitData({ packages: next });
    markSectionsUsed(["aufteilen"]);
  }

  /** Dasselbe für die Termine - sie gehören zur selben Fläche. */
  function editEvents(next: readonly ProjectEvent[]) {
    setEvents(next);
    commitData({ events: next });
    markSectionsUsed(["aufteilen"]);
  }

  /** Das Anforderungsprofil gehört zur Team-Fläche. */
  function editRequirements(next: ProjectRequirements) {
    setRequirements(next);
    commitData({ requirements: next });
  }

  /**
   * Sendet die vollständige Zugriffsliste für den geplanten Ersatz-Vertrag.
   * genesis!446 ignoriert `access` derzeit: ein erfolgreicher Aufruf bedeutet
   * im Prototyp noch nicht, dass eine Freigabe gespeichert wurde.
   */
  async function saveAccess(rows: readonly { person: { id: string }; role: ProjectRole }[]) {
    if (!projectId) return;
    const res = await updateProject({
      variables: {
        id: projectId,
        input: {
          data: {
            access: rows.map((row) => ({ person: { id: row.person.id }, ...roleToAxes(row.role) })),
          },
        },
      },
    });
    throwIfValidationRejected(res.data?.updateProject, "ValidateProjectResult", validationFallback);
  }

  function editStatus(next: ProjectStatus) {
    setStatus(next);
    commitData({ status: next });
  }

  /** Gefragte Menschen. Eigene Liste, weil sie noch keinen Zugriff haben. */
  function editCandidates(next: readonly string[]) {
    setCandidates(next);
    commitData({ candidates: next });
    // Wer jemanden ins Projekt holt, hat ein Team - die Fläche dafür soll dann
    // auch da sein, ohne dass man sie sich erst als Pille holt.
    if (next.length > 0) {
      markSectionsUsed(["mitglieder"]);
      setDone((prev) => new Set(prev).add("mitglieder"));
    }
  }

  /**
   * Nimmt Flächen dauerhaft ins Projekt auf. Aufgerufen wird das erst, wenn in
   * einer Fläche etwas entstanden ist, nicht beim Klick auf ihre Pille: wer eine
   * Fläche aufmacht, hineinschaut und weiterzieht, soll nach dem Neuladen keinen
   * leeren Reiter vorfinden.
   *
   * Mehrere Schlüssel auf einmal, weil der Assistent zwei Flächen gleichzeitig
   * abschließen kann - einzeln nacheinander gerufen würde der zweite Aufruf den
   * `sections`-Stand des ersten überschreiben, der im selben Render noch alt ist.
   *
   * Der Anlass steht hier nie drin: er ist ein Feld der Übersicht und wird als
   * `data.anlass` gespeichert.
   */
  function markSectionsUsed(keys: readonly ProjectFacetKey[]) {
    const neu = keys.filter((k) => k !== "anlass" && !sections.includes(k));
    if (neu.length === 0) return;
    const next = [...sections, ...neu];
    setSections(next);
    commitData({ sections: next });
  }

  // Finishing a screen fills its facet and hands the user back - either to the
  // next step of the assistant, or to the Project itself.
  function complete(keys: readonly ProjectFacetKey[], next: ProjectMockStep) {
    setDone((prev) => {
      const merged = new Set(prev);
      for (const key of keys) merged.add(key);
      return merged;
    });
    // Ein abgeschlossener Assistentenschritt ist der deutlichste Fall von
    // "hier ist etwas entstanden".
    markSectionsUsed(keys);
    for (const key of keys) log(key, FACET_ACTIVITY[key]);
    setStep(next);
  }

  // Reiter: Übersicht plus jede Fläche, die im Projekt liegt - in der Reihenfolge
  // der Facetten, damit die Leiste nicht je nach Klickfolge anders aussieht.
  const tabs = [
    { id: "overview", label: intl.formatMessage({ id: "project.workspace.overview", defaultMessage: "Übersicht" }) },
    ...PROJECT_FACETS.filter((f) => f.key !== "anlass" && (f.always || done.has(f.key))).map((f) => ({
      id: f.key,
      label: intl.formatMessage(f.label),
    })),
  ];


  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10">
      {/* Mit Id in der Adresse gibt es nichts anzulegen: bis das Projekt da ist, zeigt die Seite
          Laden, Fehler oder "nicht gefunden" wie ein Beitrag, nie die Anlegen-Maske (spark#1001). */}
      {routeProjectId && !loadedProject ? (
        <>
          <div className="mb-5">
            <BackButton />
          </div>
          <ShareDetailStatus
            loading={loaded.loading}
            error={loaded.error}
            notFoundLabel={intl.formatMessage({ id: "project.notFound", defaultMessage: "Projekt nicht gefunden" })}
            notFoundId="project-not-found"
          />
        </>
      ) : step === "create" ? (
        <>
          {/* Kopf wie in der Post-Maske (ShareCreatePage): Zurück, Eyebrow,
              Serifen-Überschrift. Beide Masken entstehen aus demselben Popup und
              sollen sich deshalb auch gleich anfühlen. Der Box-Titel entfällt
              dafür - die Überschrift steht jetzt darüber. */}
          <div className="mb-5">
            <BackButton />
          </div>
          <div className="mb-2 text-smycles-micro tracking-[0.25em] text-ink-muted">
            SHARE ·{" "}{/* i18n-allow: SHARE ist ein Produktname, · ein Glyph */}
            <FormattedMessage id="project.create.eyebrow" defaultMessage="Erstellen" />
          </div>
          <h1 className="mb-6 font-serif text-smycles-display-m leading-tight @sm:text-smycles-display-l">
            <FormattedMessage id="project.create.title" defaultMessage="Neues Projekt" />
          </h1>

          <div className="box max-w-[52rem] bg-surface p-6">
            <ProjectCreateScreen
                saving={creating}
                error={saveError}
                onCreate={async (created) => {
                  setSaveError(null);
                  try {
                    const response = await createProject({
                      variables: {
                        input: {
                          data: {
                            name: created.title,
                            description: created.description,
                            data: { status: "IDEA" },
                          },
                        },
                      },
                    });
                    const payload = response.data?.createProject;
                    throwIfValidationRejected(payload, "ValidateProjectResult", validationFallback);
                    const saved = payload && "result" in payload ? payload.result : null;
                    setProjectId(saved?.id ?? null);
                    // Angelegt ist angelegt: das Datum ist heute, der Ersteller bin ich.
                    // Kein Platzhalter, bis die Seite einmal neu geladen wurde.
                    setCreatedMeta({
                      at: new Date().toISOString(),
                      by: {
                        id: me.id,
                        name: me.fullName ?? "",
                        avatarContent: me.avatarContent,
                        avatarMime: me.avatarMime,
                      },
                    });
                  } catch (e) {
                    // Nie die rohe Servermeldung zeigen (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
                    setSaveError(userErrorMessage(intl, e));
                    return;
                  }
                  setProject({ title: created.title, description: created.description });
                  log("created", {
                    id: "project.activity.created",
                    defaultMessage: "Projekt angelegt",
                  });
                  // `derived` is reserved for a future assistant hand-off;
                  // ProjectCreateScreen currently always sends false.
                  // Ein frisches Projekt beginnt in der Übersicht, immer. Der
                  // Reiter steht in `?tab=` und überlebt sonst das Anlegen: wer
                  // vorher im Ablauf war, zurückging und neu anlegte, landete im
                  // Ablauf eines Projekts, das noch gar nichts hat.
                  setActiveTab("overview");
                  if (created.derived) complete(["anlass", "team"], "overview");
                  else setStep("overview");
                }}
              />
          </div>
        </>
      ) : null}

      {step === "overview" && project ? (
        <ProjectWorkspace
          readOnly={readOnly}
          onDelete={projectId && actions.canDelete ? () => setConfirmDelete(true) : undefined}
          onOpenSettings={() => setSettingsOpen(true)}
          onEdit={editProject}
          onCommit={commitProject}
          saveState={saveState}
          lastSavedAt={lastSavedAt}
          onRetrySave={() => void flush()}
          project={project}
          done={done}
          activity={activity}
          status={status}
          onStatusChange={editStatus}
          anlassId={anlassId}
          createdMeta={createdMeta}
          tags={tags}
          onTagsChange={(next) => {
            setTags(next);
            commitData({ tags: next });
          }}
          onAnlassChange={(id) => {
            setAnlassId(id);
            commitData({ anlass: id });
            complete(["anlass"], "overview");
          }}
          onShowActivity={() => {
            markSectionsUsed(["ablauf"]);
            setDone((prev) => new Set(prev).add("ablauf"));
            setActiveTab("ablauf");
          }}
          tabs={tabs}
          activeTab={activeTab}
          onSelectTab={setActiveTab}
          onAddBlock={(key) => {
            // Hinzufügen heißt hingehen: die neue Fläche wird gleich geöffnet,
            // sonst klickt man zweimal für eine Sache.
            //
            // Und sie BLEIBT, auch wenn man nichts einträgt. Vorher wurde sie
            // erst bei der ersten Änderung gespeichert, damit kein leerer Reiter
            // zurückbleibt. In der Hand fühlt sich das falsch an: Wer eine Fläche
            // holt, hat eine Entscheidung getroffen, und dass sie beim nächsten
            // Laden wieder weg ist, liest sich wie ein verlorener Klick.
            setDone((prev) => new Set(prev).add(key));
            markSectionsUsed([key]);
            setActiveTab(key);
          }}
        >
          {/* Die Blöcke leben hier, weil hier ihr Zustand liegt. Der Workspace
              stellt nur den Platz. Jeder Block ist die Arbeitsfläche selbst,
              nicht ein Link auf einen eigenen Screen. */}
          {activeTab === "aufteilen" ? (
            <ProjectGuidedAufteilenScreen
              packages={packages}
              events={events}
              people={people.assignable}
              involved={people.involved}
              lookupPerson={people.lookup}
              onChange={editPackages}
              onEventsChange={editEvents}
              readOnly={readOnly}
            />
          ) : null}
          {activeTab === "team" ? (
            <ProjectGuidedTeamScreen
              requirements={requirements}
              onChange={editRequirements}
              readOnly={readOnly}
            />
          ) : null}
          {activeTab === "dokumente" ? (
            <ProjectDocuments
              documents={documents}
              onChange={(next) => {
                setDocuments(next);
                commitData({ documents: next });
                markSectionsUsed(["dokumente"]);
              }}
              readOnly={readOnly}
            />
          ) : null}
          {activeTab === "mitglieder" ? (
            <ProjectTeamScreen
              owner={owner}
              members={others}
              candidates={candidates}
              onRemoveCandidate={(id) => editCandidates(candidates.filter((c) => c !== id))}
              onGoToMatch={(next) => {
                setDone((prev) => new Set(prev).add("kandidaten"));
                setCandidatePath(next);
                setActiveTab("kandidaten");
              }}
              readOnly={readOnly}
            />
          ) : null}
          {activeTab === "kandidaten" ? (
            <ProjectCandidatesScreen
              candidates={candidates}
              onCandidatesChange={editCandidates}
              linkedMatches={linkedMatches}
              path={candidatePath}
              onPathChange={setCandidatePath}
              onUnlinkMatch={
                projectId && !readOnly
                  ? async (matchId) => {
                      await unlinkProjectMatch({ variables: { input: { projectId, matchId } } });
                    }
                  : undefined
              }
              readOnly={readOnly}
              matchWizard={
                project ? (
                  <ProjectToMatchScreen
                    projectId={projectId}
                    title={project.title}
                    description={project.description}
                    requirements={requirements}
                    people={people.assignable}
                    documents={documents}
                  />
                ) : null
              }
            />
          ) : null}
          {activeTab === "ablauf" ? <ProjectActivity items={activity} /> : null}
        </ProjectWorkspace>
      ) : null}

      {/* Löschen ist endgültig und betrifft das ganze Objekt, deshalb die
          Rückfrage im Dialog statt der zweistufigen Inline-Variante, die für
          einzelne Zeilen gedacht ist (@adr SPARK-0011-destructive-actions-require-confirmation@1).
          Die verknüpften Matches bleiben: sie sind eigene Objekte mit eigener
          Arbeit darin. */}
      {settingsOpen && project ? (
        <ProjectSettingsModal
          title={project.title}
          description={project.description}
          status={status}
          access={accessRows}
          owner={createdMeta?.by ?? null}
          canEdit={actions.canManageAccess}
          onSave={async (next) => {
            editProject({ title: next.title, description: next.description });
            commitProject();
            if (next.status !== status) editStatus(next.status);
            await saveAccess(next.access);
          }}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      <ConfirmDialog
        id="project-delete-dialog"
        open={confirmDelete}
        title={<FormattedMessage id="project.delete.title" defaultMessage="Projekt löschen" />}
        message={
          <>
            <FormattedMessage
              id="project.delete.confirm"
              defaultMessage="Dieses Projekt endgültig löschen? Verknüpfte Matches bleiben bestehen."
            />
            {deleteError && <p id="project-delete-dialog-error" role="alert" className="mt-3">{deleteError}</p>}
          </>
        }
        confirmLabel={<FormattedMessage id="common.delete" defaultMessage="Löschen" />}
        onConfirm={async () => {
          if (!projectId || deleting) return;
          setDeleteError(null);
          try {
            await deleteProject({ variables: { input: { id: projectId } } });
          } catch (e) {
            // Nie die rohe Servermeldung zeigen (@adr SPARK-0010-no-raw-server-errors-in-ui@1).
            setDeleteError(userErrorMessage(intl, e));
            return;
          }
          setConfirmDelete(false);
          navigate("/share");
        }}
        onCancel={() => {
          setConfirmDelete(false);
          setDeleteError(null);
        }}
      />
    </main>
  );
}
